import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { errorText } from '../../utils/errorText';
import { barLength, MIN_BAR_PX, scaleChange, scaleFromBar } from '../../utils/microscopy/scaleBar';
import Button from '../ui/Button';

/*
 * Measure the bar burned into a micrograph: draw along it on the picture, type
 * its length, say why. The picture's owner (MicroscopyViewer, the workbench's
 * microscopy mode) holds the line and the drawing; `measuring` says the pointer
 * draws, and onMeasure turns that on or off.
 *
 * The server computes the scale from these two numbers and keeps them
 * (POST /microscopy/{id}/scale; scale_source 'scale_bar'). The form says what
 * they will give, and how far that stands from the scale there is now, before
 * anything is saved. On a layer of a Z-stack it can also give the scale to the
 * stack's layers with none: they were taken together, and the server leaves a
 * layer alone that has a scale or was taken another way.
 */
const WARN_CHANGE = 0.05;
const toFixed = (v, n = 4) => Number(v).toPrecision(n);

export default function ScaleBarForm({ image, line, measuring, onMeasure, onSaved }) {
  const { t } = useTranslation('scan');
  const [um, setUm] = useState('');
  const [reason, setReason] = useState('');
  const [allLayers, setAllLayers] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const px = barLength(line);
  const next = scaleFromBar(um, line);
  const current = image.um_per_px ? Number(image.um_per_px) : null;
  const change = scaleChange(next, current);
  const canSave = !!next && reason.trim().length >= 3 && !busy;
  const canDraw = !!image.width_px && !!image.height_px;

  const save = async () => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const body = { scale_ref_um: Number(um), scale_ref_line: line, reason: reason.trim() };
      if (allLayers) body.all_layers = true;
      const { data } = await api.post(`/microscopy/${image.image_id}/scale`, body);
      setUm('');
      setReason('');
      if (allLayers) setNotice(t('scaleBar.layers', { copied: data.layers_copied.length, kept: data.layers_kept }));
      onSaved(data);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="scale-bar-form" style={sectionStyle} aria-label={t('scaleBar.title')}>
      <p style={mutedStyle} data-testid="scale-bar-current">
        {current
          ? <><bdi dir="ltr">{toFixed(current)} µm/px</bdi> · {t(`scale_${image.scale_source}`)}</>
          : t('scale_none')}
        {image.scale_ref_um
          ? <> · <bdi dir="ltr">{t('scaleBar.measured', { um: image.scale_ref_um, px: Math.round(image.scale_ref_px) })}</bdi></>
          : null}
      </p>
      {canDraw ? <p style={mutedStyle}>{t('scaleBar.help')}</p> : <p role="note" style={mutedStyle}>{t('scaleBar.noSize')}</p>}
      <div style={rowStyle}>
        <Button variant={measuring ? 'primary' : 'secondary'} aria-pressed={measuring} disabled={!canDraw}
                data-testid="scale-bar-draw" onClick={() => onMeasure(!measuring)}>
          📐 {t(measuring ? 'scaleBar.drawing' : 'scaleBar.draw')}
        </Button>
        <span data-testid="scale-bar-px" style={{ fontSize: 13 }}>
          {px ? <bdi dir="ltr">{Math.round(px)} px</bdi> : t('scaleBar.noLine')}
        </span>
      </div>
      {px > 0 && px < MIN_BAR_PX && (
        <p role="alert" data-testid="scale-bar-short" style={warnStyle}>{t('scaleBar.tooShort', { min: MIN_BAR_PX })}</p>
      )}
      <label style={fieldStyle}>
        <span>{t('scaleBar.length')}</span>
        <input type="number" min={0} step="any" value={um} onChange={e => setUm(e.target.value)} style={inputStyle}
               data-testid="scale-bar-um" dir="ltr" title={t('tip.scaleBarLength')} />
      </label>
      {next && (
        <p role="status" data-testid="scale-bar-gives" style={{ fontSize: 13, margin: 0 }}>
          {t('scaleBar.gives')} <bdi dir="ltr">{toFixed(next)} µm/px</bdi>
        </p>
      )}
      {change !== null && change > WARN_CHANGE && (
        <p role="alert" data-testid="scale-bar-differs" style={warnStyle}>
          {t('scaleBar.differs', { percent: Math.round(change * 100) })}
        </p>
      )}
      {image.bundle_id && (
        <label style={checkStyle}>
          <input type="checkbox" checked={allLayers} onChange={e => setAllLayers(e.target.checked)}
                 data-testid="scale-bar-all-layers" />
          <span>{t('scaleBar.allLayers')}</span>
        </label>
      )}
      <label style={fieldStyle}>
        <span>{t('reason')}</span>
        <input value={reason} onChange={e => setReason(e.target.value)} style={inputStyle} data-testid="scale-bar-reason"
               title={t('tip.reason')} />
      </label>
      <div style={rowStyle}>
        <Button variant="primary" disabled={!canSave} data-testid="scale-bar-save" onClick={save}>{t('saveScale')}</Button>
      </div>
      {notice && <p role="status" data-testid="scale-bar-notice" style={{ fontSize: 13, margin: 0 }}>{notice}</p>}
      {error && <p role="alert" style={warnStyle}>{error}</p>}
    </section>
  );
}

const sectionStyle = { display: 'flex', flexDirection: 'column', gap: 8, padding: '8px 0' };
const rowStyle = { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' };
const fieldStyle = { display: 'flex', flexDirection: 'column', gap: 2, fontSize: 13 };
const checkStyle = { display: 'flex', gap: 6, alignItems: 'flex-start', fontSize: 13 };
const inputStyle = { padding: '4px 6px', borderRadius: 6, border: '1px solid var(--c-border-strong)', maxWidth: '100%' };
const mutedStyle = { fontSize: 13, margin: 0, color: 'var(--c-text-muted-fg)' };
const warnStyle = { fontSize: 13, margin: 0, color: 'var(--c-danger-fg)' };
